import React, { useEffect, useState } from 'react';
import { Box, Link, TextField, TextFieldProps, Typography } from '@mui/material';
import { ReportTemplate } from '../../types/projectReport';
import { dateLabel, differsFromSap, formatAmount, parseAmount } from '../../utils/projectReport';

/**
 * The monthly report's inputs. A field SAP / MCSR fills in says so under it ("From SAP"); when the
 * project manager changed it, it shows SAP's figure with "use" to take it back.
 */

export interface SapHintProps {
  /** SAP's value for this field; undefined = not a SAP field. */
  sap?: string | number | null;
  onUseSap?: () => void;
  /** How to print SAP's value (amounts, dates). */
  formatSap?: (v: string | number) => string;
}

function sapHelper(value: unknown, { sap, onUseSap, formatSap }: SapHintProps, disabled?: boolean): React.ReactNode {
  if (sap === undefined || sap === null || sap === '') return undefined;
  if (!differsFromSap(value, sap)) return <Typography component="span" variant="caption" color="success.main">From SAP</Typography>;
  const shown = formatSap ? formatSap(sap) : String(sap);
  return (
    <Typography component="span" variant="caption" color="warning.main">
      Changed. SAP: {shown}
      {!disabled && onUseSap && (
        <>
          {' · '}
          <Link component="button" type="button" variant="caption" onClick={onUseSap}>use SAP value</Link>
        </>
      )}
    </Typography>
  );
}

type Common = SapHintProps & {
  label: string;
  disabled?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  inputLabel?: string;
};

export const TextInput: React.FC<Common & {
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  minRows?: number;
  maxLength?: number;
}> = ({ label, value, onChange, disabled, multiline, minRows, maxLength, helperText, sap, onUseSap, formatSap, size = 'small' }) => (
  <TextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    multiline={multiline}
    minRows={minRows}
    fullWidth
    size={size}
    inputProps={{ maxLength: maxLength ?? 500 }}
    helperText={helperText ?? sapHelper(value, { sap, onUseSap, formatSap }, disabled)}
  />
);

export const DateInput: React.FC<Common & { value: string | null; onChange: (v: string | null) => void }> = ({
  label, value, onChange, disabled, helperText, sap, onUseSap, size = 'small',
}) => (
  <TextField
    label={label}
    type="date"
    value={value || ''}
    onChange={(e) => onChange(e.target.value || null)}
    disabled={disabled}
    fullWidth
    size={size}
    InputLabelProps={{ shrink: true }}
    inputProps={{ min: '1990-01-01', max: '2100-12-31' }}
    helperText={helperText ?? sapHelper(value, { sap, onUseSap, formatSap: (v) => dateLabel(String(v)) }, disabled)}
  />
);

/**
 * A number typed freely (commas allowed), saved as a number on leaving the field; shown with
 * thousands separators (and the currency's decimals for amounts) while not being edited.
 */
export const NumberInput: React.FC<Common & {
  value: number | null;
  onChange: (v: number | null) => void;
  /** Format as an amount of this template's currency. */
  template?: ReportTemplate;
  integer?: boolean;
  suffix?: string;
  textFieldProps?: Partial<TextFieldProps>;
}> = ({ label, inputLabel, value, onChange, disabled, helperText, template, integer, suffix, sap, onUseSap, size = 'small', textFieldProps }) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value));
  useEffect(() => {
    if (!focused) setText(value === null || value === undefined ? '' : String(value));
  }, [value, focused]);
  const shown = focused
    ? text
    : value === null || value === undefined
      ? ''
      : template
        ? formatAmount(value, template)
        : value.toLocaleString('en-US');
  const commit = () => {
    setFocused(false);
    let n = parseAmount(text);
    if (n !== null && integer) n = Math.round(n);
    if (n !== value) onChange(n);
  };
  return (
    <TextField
      label={label || undefined}
      value={shown}
      onFocus={() => setFocused(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      disabled={disabled}
      fullWidth
      size={size}
      inputProps={{ inputMode: integer ? 'numeric' : 'decimal', 'aria-label': inputLabel || label, style: { textAlign: label ? undefined : 'right' } }}
      InputProps={suffix ? { endAdornment: <Typography variant="body2" color="text.secondary">{suffix}</Typography> } : undefined}
      helperText={helperText ?? sapHelper(value, { sap, onUseSap, formatSap: (v) => (template ? formatAmount(Number(v), template) : String(v)) }, disabled)}
      {...textFieldProps}
    />
  );
};

/** A computed value shown like a read-only field. */
export const ComputedValue: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <Box sx={{ px: 1.5, py: 0.75, borderRadius: 1, bgcolor: 'action.hover', minHeight: 40 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value || '—'}</Typography>
    {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
  </Box>
);
